import {
  appRoutes,
  DEFAULT_DATE_FORMAT,
  DEFAULT_VALUE_IF_NIL,
  RouterEntitiesId,
} from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatDate } from '@clearverify/utils';

import {
  CurrentUserBaseInfoFragment,
  DataDisputeItemsListQuery,
  DataDisputeItemWhere,
  VerificationRequestsListQuery,
  VerificationRequestWhere,
} from 'src/api';
import { UserPermissions } from 'src/constants/user';
import { buildInboxItemFilterByEmployee } from 'src/utils/inboxItem';
import { buildUrl } from 'src/utils/router';
import { getUserFullName } from 'src/utils/user';

import { GetInTouchFormValues } from '../types';

export const formatVerificationRequestOptions = (
  data: Nullable<VerificationRequestsListQuery>,
  userPermissions: UserPermissions,
) => {
  const verificationRequestItems = data?.verificationRequestList;
  if (!verificationRequestItems) {
    return [];
  }

  if (userPermissions.hasVerifierRoles || userPermissions.hasEmployerRoles) {
    return verificationRequestItems.map(verificationRequest => {
      const consumerName =
        getUserFullName(verificationRequest.employee.user) || DEFAULT_VALUE_IF_NIL;

      const createdDate = formatDate(verificationRequest.createdAt, DEFAULT_DATE_FORMAT);

      return {
        label: `Verification Request for ${consumerName} created at ${createdDate}`,
        value: `${window.origin}${buildUrl(appRoutes.inbox.inboxItem, {
          [RouterEntitiesId.inboxItem]: verificationRequest.inboxItemId,
        })}`,
      };
    });
  }

  if (userPermissions.isEmployee) {
    return verificationRequestItems.map(verificationRequest => {
      const verifierCompanyName =
        verificationRequest.verifierStaff.verifier.displayName || DEFAULT_VALUE_IF_NIL;
      const createdDate =
        formatDate(verificationRequest.createdAt, DEFAULT_DATE_FORMAT) || DEFAULT_VALUE_IF_NIL;

      return {
        label: `Verification Request for ${verifierCompanyName} created at ${createdDate}`,
        value: `${window.origin}${buildUrl(appRoutes.inbox.inboxItem, {
          [RouterEntitiesId.inboxItem]: verificationRequest.inboxItemId,
        })}`,
      };
    });
  }

  return [];
};

export const formatDataDisputeItemsOptions = (
  data: Nullable<DataDisputeItemsListQuery>,
  userPermissions: UserPermissions,
) => {
  const dataDisputeItems = data?.dataDisputeItemList;

  if (!dataDisputeItems) {
    return [];
  }

  if (userPermissions.hasEmployerRoles) {
    return dataDisputeItems.map(dataDisputeItem => {
      const consumerName =
        getUserFullName(dataDisputeItem.dataDispute.employee.user) || DEFAULT_VALUE_IF_NIL;
      const createdDate =
        formatDate(dataDisputeItem.createdAt, DEFAULT_DATE_FORMAT) || DEFAULT_VALUE_IF_NIL;

      return {
        label: `Data Dispute from ${consumerName} created at ${createdDate}`,
        value: `${window.origin}${buildUrl(appRoutes.inbox.inboxItem, {
          [RouterEntitiesId.inboxItem]: dataDisputeItem.dataDispute.inboxItemId,
        })}`,
      };
    });
  }

  if (userPermissions.isEmployee) {
    return dataDisputeItems.map(dataDisputeItem => {
      const employerDisplayName = dataDisputeItem.employer.displayName || DEFAULT_VALUE_IF_NIL;
      const createdDate =
        formatDate(dataDisputeItem.createdAt, DEFAULT_DATE_FORMAT) || DEFAULT_VALUE_IF_NIL;

      return {
        label: `Data Dispute with ${employerDisplayName} created at ${createdDate}`,
        value: `${window.origin}${buildUrl(appRoutes.inbox.inboxItem, {
          [RouterEntitiesId.inboxItem]: dataDisputeItem.dataDispute.inboxItemId,
        })}`,
      };
    });
  }

  return [];
};

export const getVerificationRequestVariablesByRole = (
  businessRoleId: Nullable<string>,
  userPermissions: UserPermissions,
) => {
  const filterVariable: {
    variables: {
      filter: VerificationRequestWhere;
    };
  } = {
    variables: {
      filter: {},
    },
  };

  if (userPermissions.hasEmployerRoles) {
    filterVariable.variables.filter = {
      inboxItem: buildInboxItemFilterByEmployee({
        positions: {
          some: {
            employerId: { equals: businessRoleId },
          },
        },
      }),
    };
  }

  if (userPermissions.hasVerifierRoles) {
    filterVariable.variables.filter = {
      verifierStaff: {
        verifierId: { equals: businessRoleId },
      },
    };
  }

  if (userPermissions.isEmployee) {
    filterVariable.variables.filter = {
      inboxItem: buildInboxItemFilterByEmployee({
        id: { equals: businessRoleId },
      }),
    };
  }

  return filterVariable;
};

export const getDataDisputeItemsListVariablesByRole = (
  businessRoleId: Nullable<string>,
  userPermissions: UserPermissions,
) => {
  const filterVariable: {
    variables: {
      filter: DataDisputeItemWhere;
    };
  } = {
    variables: {
      filter: {},
    },
  };

  if (userPermissions.hasEmployerRoles) {
    filterVariable.variables.filter = {
      employerId: { equals: businessRoleId },
    };
  }

  if (userPermissions.isEmployee) {
    filterVariable.variables.filter = {
      dataDispute: {
        inboxItem: buildInboxItemFilterByEmployee({
          id: { equals: businessRoleId },
        }),
      },
    };
  }

  return filterVariable;
};

export const prepareGetInTouchFormData = (
  data: GetInTouchFormValues,
  user: Nullable<CurrentUserBaseInfoFragment>,
) => {
  const attachments = data.attachments.map(attachment => attachment.id);

  return {
    ...data,
    attachments,
    user: {
      name: getUserFullName(user) ?? '',
      email: user?.email ?? '',
    },
  };
};
